import { ArrowLeftToLine, DoorOpen, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import { cn } from "@/lib/utils";
import {
  type BreakoutRoom,
  type ModerationParticipant,
} from "@/zustand/CallModerationState";

type Props = {
  /** The breakout room this card renders (never the main room). */
  room: BreakoutRoom;
  /** Other breakout rooms (move-between targets); the current room is excluded by the caller. */
  otherRooms: BreakoutRoom[];
  /** The main room's map-key id (the `sendParticipantToRoom` target for "send to main"), or
   * `undefined` when the main room hasn't been observed yet — then no send-to-main is offered. */
  mainRoomId: string | undefined;
  /** Remote main-conference roster — the correct source for "add someone from main": those people
   * are still in the main MUC, so their endpoint id resolves via `getParticipantById`. */
  rosterParticipants: ModerationParticipant[];
  /** Local participant endpoint id — used to detect the moderator's own occupant row so it is not
   * given a self-move (which no-ops); returning to main is a section-level control instead. */
  localParticipantId: string | null;
  /** `sendParticipantToRoom(participantId, roomId)`. For a breakout OCCUPANT pass the full occupant
   * JID (`occupant.jid`); for an add-from-main person pass their roster endpoint id. */
  sendParticipantToRoom: (participantId: string, roomId: string) => void;
  /** `joinBreakoutRoom(roomId)` — moderator self-visit. */
  joinBreakoutRoom: (roomId: string) => void;
  /** Close (remove) this room — `removeBreakoutRoom(jid)` with the close-by-key fallback. */
  closeRoom: (room: Pick<BreakoutRoom, "id" | "jid">) => void;
};

/**
 * One breakout-room card inside the moderation panel. The occupant rows are the ACTION SURFACE
 * (driven off `room.participants`, the `breakoutRoomsUpdated` source of truth) — this is the P0
 * fix: a participant who has left the main MUC on a move is still actionable here, whereas the old
 * main-roster-sourced move control lost them. See `BreakoutRoomsSection` for the verified command
 * contract.
 */
export default function BreakoutRoomCard({
  room,
  otherRooms,
  mainRoomId,
  rosterParticipants,
  localParticipantId,
  sendParticipantToRoom,
  joinBreakoutRoom,
  closeRoom,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const roomName =
    room.name || t("videoCall.moderation.breakout.roomFallbackName");

  return (
    <li className="rounded-md border border-foreground/10 p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium" title={roomName}>
          {roomName}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          {/* Moderator self-visit: joinBreakoutRoom(room.id) moves the moderator into this room.
              Gated on a known main-room id so we never offer a one-way door — the section-level
              "Return to main" (joinBreakoutRoom(mainRoomId)) needs that same id to exist. */}
          {mainRoomId !== undefined && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-8 text-foreground/70 hover:bg-secondary/15 hover:text-secondary"
              aria-label={t("videoCall.moderation.breakout.visitLabel", {
                room: roomName,
              })}
              title={t("videoCall.moderation.breakout.visit")}
              onClick={() => joinBreakoutRoom(room.id)}
            >
              <DoorOpen className="size-4" />
            </Button>
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-8 text-foreground/70 hover:bg-destructive/15 hover:text-destructive"
            aria-label={t("videoCall.moderation.breakout.closeRoomLabel", {
              room: roomName,
            })}
            title={t("videoCall.moderation.breakout.closeRoom")}
            onClick={() => closeRoom(room)}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {room.participants.length === 0 ? (
        <p className="mt-1 px-1 text-xs text-foreground/40">
          {t("videoCall.moderation.breakout.noRoomParticipants")}
        </p>
      ) : (
        <ul className="mt-1 flex flex-col gap-1">
          {room.participants.map((participant) => {
            const isSelf =
              localParticipantId !== null &&
              participant.id === localParticipantId;
            return (
              <li
                key={participant.id}
                className="flex items-center justify-between gap-2"
              >
                <span
                  className="min-w-0 flex-1 truncate px-1 text-xs text-foreground/70"
                  title={participant.displayName}
                >
                  {participant.displayName}
                  {isSelf && (
                    <span className="ml-1 text-foreground/40">
                      ({t("videoCall.moderation.breakout.you")})
                    </span>
                  )}
                </span>

                {/* The moderator's own row gets no self-move (it no-ops) — returning to main is a
                    section-level control. Everyone else gets send-to-main + move-between, sourced
                    off room.participants and passing the FULL occupant JID. The actions are gated on
                    a real occupant JID (contains "@"): only that value resolves via _findParticipantJid;
                    a bare endpoint id would silently no-op, so we hide the controls rather than offer a
                    dead button (the empty-jid case is defensive — never seen in the verified payload). */}
                {!isSelf && participant.jid !== "" && (
                  <div className="flex shrink-0 items-center gap-1">
                    {mainRoomId !== undefined && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-7 text-foreground/70 hover:bg-secondary/15 hover:text-secondary"
                        aria-label={t(
                          "videoCall.moderation.breakout.sendToMainLabel",
                          { name: participant.displayName },
                        )}
                        title={t("videoCall.moderation.breakout.sendToMain")}
                        onClick={() =>
                          sendParticipantToRoom(participant.jid, mainRoomId)
                        }
                      >
                        <ArrowLeftToLine className="size-3.5" />
                      </Button>
                    )}
                    {otherRooms.length > 0 && (
                      <select
                        aria-label={t(
                          "videoCall.moderation.breakout.moveToLabel",
                          { name: participant.displayName },
                        )}
                        // Pure action trigger: snaps back to the placeholder after each move.
                        value=""
                        onChange={(event) => {
                          const target = event.target.value;
                          if (!target) {
                            return;
                          }
                          sendParticipantToRoom(participant.jid, target);
                        }}
                        className={cn(
                          "h-7 rounded-md border border-input bg-transparent px-1 text-xs",
                          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                        )}
                      >
                        <option value="">
                          {t("videoCall.moderation.breakout.moveToPlaceholder")}
                        </option>
                        {otherRooms.map((target) => (
                          <option key={target.id} value={target.id}>
                            {target.name ||
                              t(
                                "videoCall.moderation.breakout.roomFallbackName",
                              )}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add someone from main into this room. Correctly sourced from the main-conference roster:
          those people are still in the main MUC, so their endpoint id resolves via
          getParticipantById (unlike breakout occupants, which need the full JID above). */}
      {rosterParticipants.length > 0 && (
        <select
          aria-label={t("videoCall.moderation.breakout.assignLabel", {
            room: roomName,
          })}
          value=""
          onChange={(event) => {
            const personId = event.target.value;
            if (!personId) {
              return;
            }
            sendParticipantToRoom(personId, room.id);
          }}
          className={cn(
            "mt-2 h-8 w-full rounded-md border border-input bg-transparent px-2 text-xs",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          )}
        >
          <option value="">
            {t("videoCall.moderation.breakout.assignPlaceholder")}
          </option>
          {rosterParticipants.map((participant) => (
            <option key={participant.id} value={participant.id}>
              {participant.displayName}
            </option>
          ))}
        </select>
      )}
    </li>
  );
}
